<template>
  <AreaPageContainer
    v-if="area"
  >
      <!-- Content wrapper with header and footer -->
      <AreaPageBody>
        <AreaHeader />

        <!-- Inner Content -->
        <div class="tw-p-10">
          <!-- Title -->
          <div>
            <div class="tw-flex tw-justify-between">
              <h2 class="tw-font-georgia tw-font-bold tw-text-5xl">
                Fees
              </h2>
              <div
                v-if="area.cumulative_fee_difference && !isNaN(area.cumulative_fee_difference)"
                class="tw-flex tw-flex-col"
              >
                <div class="has-text-right moneysaved">
                  <p class="heading is-uppercase has-text-grey">
                    20-yr Difference<template v-if="area.feeDifferenceComparison">
                      ({{ area.feeDifferenceComparison.from }} vs. {{ area.feeDifferenceComparison.to }})
                    </template>
                  </p>
                  <p class="tw-font-bold tw-text-2xl is-family-code">
                    {{ (isNaN(area.cumulative_fee_difference) ? Math.abs(area.cumulative_fee_difference) : Math.abs(area.cumulative_fee_difference)?.toLocaleString('en-US', {style: 'currency', currency: 'USD', maximumFractionDigits: 0})) || '' }}
                  </p>
                  <p
                    v-if="feesV2Enabled"
                    class="tw-text-xs tw-italic tw-text-gray-500 tw-mt-1"
                  >
                    Hypothetical projection — see methodology
                  </p>

                  <!-- Looks bad if there are a lot of items like with sector breakdown. For now only show if just one. -->
                  <!-- <div v-if="area.current?.length === 1" v-for="item in area.current" class="subtitle is-family-code ">
              <div :class="item?.value > 0 ? 'tw-text-green-500' : 'tw-text-red-500'">{{ item?.value?.toFixed(2) || '' }}</div>
            </div> -->
                </div>
              </div>
            </div>
            <AdvisorCoreStripes />
          </div>

          <!-- Text -->
          <p class="tw-font-poppins tw-mt-10">
            {{ area.headerText }}
          </p>

          <!-- Chart -->
          <div class="tw-mt-10">
            <div :class="{'tw-flex': true, 'tw-flex-col': !area.showChartsAsRow}">
              <div
                v-for="(chart, i) in area.chartData"
                :key="i"
                class="tw-grow"
              >
                <div v-if="chart?.library === 'highcharts'">
                  <HighChartsWrapper
                    :name="area.name"
                    :chart-options="chart"
                    export-image
                    @image-changed="(data) => checkupStore.updateSlideImage(data, area.name)"
                    @image-removed="(data) => checkupStore.removeSlideImage(data)"
                  />
                </div>
                <ChartWrapper
                  v-else
                  canvas-class="tw-max-h-96"
                  :name="area.name"
                  :chart-config="chart"
                  @chartClick="handleChartClick"
                  @imageChanged="(data) => checkupStore.updateChart(data, area.name)"
                />
              </div>
            </div>
            <table
              v-if="!feesV2Enabled"
              class="tw-mt-10 tw-w-full tw-border-collapse tw-border tw-border-gray-200"
            >
              <thead>
                <tr class="tw-bg-gray-100">
                  <th class="tw-border tw-border-gray-200 tw-py-2 tw-px-4">
                    Fees and Expenses
                  </th>
                  <th
                    v-for="header in [...new Set(area.extraData.map(data => data.name.replace('Advisory and Mgmt Fees', '').replace('Avg Expense Ratio', '').trim()))]"
                    :key="header"
                    class="tw-border tw-border-gray-200 tw-py-2 tw-px-4"
                  >
                    {{ header }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="tw-border tw-border-gray-200 tw-py-2 tw-px-4">Avg Expense Ratio</td>
                  <td
                    v-for="header in [...new Set(area.extraData.map(data => data.name.replace('Advisory and Mgmt Fees', '').replace('Avg Expense Ratio', '').trim()))]"
                    :key="'avg-' + header"
                    class="tw-border tw-border-gray-200 tw-py-2 tw-px-4 tw-cursor-pointer"
                    @click="handleModalClick('Avg Expense Ratio', header)"
                  >
                    {{
                      (area.extraData.find(data => data.name.includes(header) && data.name.includes('Avg Expense Ratio')) || {}).value || '--'
                    }}
                  </td>
                </tr>
                <tr>
                  <td class="tw-border tw-border-gray-200 tw-py-2 tw-px-4">Advisory and Mgmt Fees</td>
                  <td
                    v-for="header in [...new Set(area.extraData.map(data => data.name.replace('Advisory and Mgmt Fees', '').replace('Avg Expense Ratio', '').trim()))]"
                    :key="'mgmt-' + header"
                    class="tw-border tw-border-gray-200 tw-py-2 tw-px-4"
                  >
                    {{
                      (area.extraData.find(data => data.name.includes(header) && data.name.includes('Advisory and Mgmt Fees')) || {}).value || '--'
                    }}
                  </td>
                </tr>
              </tbody>
            </table>

            <ChartClickthroughs
              :click-data="chartClickData"
              :get-table-data="area.clickthroughFunction"
            />
          </div>

          <!-- Fee Breakdown Table -->
          <div
            v-if="feesV2Enabled && portfolioHeaders.length"
            class="tw-mt-10"
          >
            <table class="tw-w-full tw-border-collapse tw-border tw-border-gray-200 tw-text-sm">
              <thead>
                <tr class="tw-bg-gray-100">
                  <th class="tw-border tw-border-gray-200 tw-py-2 tw-px-4 tw-text-left">Fees and Expenses</th>
                  <th
                    v-for="header in portfolioHeaders"
                    :key="header"
                    class="tw-border tw-border-gray-200 tw-py-2 tw-px-4"
                  >
                    {{ header }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in feeTableRows"
                  :key="row.key"
                  :class="row.isTotal ? 'tw-font-semibold tw-bg-gray-50' : ''"
                >
                  <td class="tw-border tw-border-gray-200 tw-py-2 tw-px-4">{{ row.label }}</td>
                  <td
                    v-for="header in portfolioHeaders"
                    :key="header"
                    class="tw-border tw-border-gray-200 tw-py-2 tw-px-4 tw-text-center"
                    :class="row.key === 'avg' ? 'tw-cursor-pointer' : ''"
                    @click="row.key === 'avg' ? handleModalClick('Avg Expense Ratio', header) : null"
                  >
                    {{ row.key === 'advMgmt' ? advisoryMgmtCellValue(header) : cellValue(header, row.suffix) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Disclaimer and Methodology -->
          <div class="tw-flex tw-gap-10">
            <!-- <CheckupDisclaimer class="tw-basis-0 tw-grow tw-bg-white !tw-text-[#353a46] tw-px-0" /> -->

            <div class="tw-flex tw-flex-col tw-basis-0 tw-grow tw-py-10 tw-bg-white !tw-text-[#353a46]">
              <h3 class="tw-text-xl tw-font-bold tw-font-georgia">
                Methodology
              </h3>
              <CheckupMethodologyWrapper
                :component-name="area.methodology"
                display-type="short"
                class="tw-text-xs tw-font-poppins"
              />
              <CheckupMethodologyWrapper
                :component-name="area.methodology"
                display-type="long"
                class="tw-text-xs tw-font-poppins tw-hidden"
              />
            </div>
          </div>
        </div>

        <AreaFooter />
        <CheckupDisclaimer class="tw-mt-16">
          <CheckupMethodologyWrapper
            :component-name="area.methodology"
            display-type="long"
          />
        </CheckupDisclaimer>
      </AreaPageBody>

      <!-- Settings Sidebar -->
      <AreaSidebar :area="area" :show-fees-toggle="feesV2Enabled">
        <IncludeInPresentationToggle :name="area?.name" />
        <!-- Flag off: render the legacy advisory-fee inputs inline (always visible),
             matching pre-feature behavior. Flag on: tuck them behind the
             "Set custom fees" toggle via the #fees slot. -->
        <AdvisorFeeInput v-if="!feesV2Enabled" />
        <template #fees>
          <AdvisorFeeInput />
        </template>
        <template #other>
          <SpeakersNotes :notes="area?.explanation" />
        </template>
      </AreaSidebar>
  </AreaPageContainer>

  <div v-else>
    <p>Loading...</p>
  </div>
</template>


<script>
import { GRADE_COLORS } from '@frontend/ui/features/checkup/helpers/checkupFunctions.js';
import { useChatStore } from '@frontend/ui/stores/chatStore';
import { useCheckupStore } from '@frontend/ui/stores/checkupStore';
import { mapStores } from 'pinia';

import AreaPageContainer from '@frontend/ui/features/checkup/components/analytics/shared/AreaPageContainer.vue';
import AreaPageBody from '@frontend/ui/features/checkup/components/analytics/shared/AreaPageBody.vue';
import ChartWrapper from '@frontend/ui/components/charts/ChartWrapper.vue';
import ChartClickthroughs from '@frontend/ui/features/checkup/components/ChartClickthroughs.vue';
import CheckupDisclaimer from '@frontend/ui/features/checkup/components/CheckupDisclaimer.vue';
import FeesMethodology from '@frontend/ui/features/checkup/components/methodologies/FeesMethodology.vue';

import HighChartsWrapper from '@frontend/ui/components/charts/HighChartsWrapper.vue';
import AreaSidebar from '@frontend/ui/features/checkup/components/sidebar/AreaSidebar.vue';
import AdvisorCoreStripes from '@frontend/ui/features/checkup/components/AdvisorCoreStripes.vue';
import IncludeInPresentationToggle from '@frontend/ui/features/checkup/components/sidebar/IncludeInPresentationToggle.vue';
import SpeakersNotes from '@frontend/ui/features/checkup/components/sidebar/SpeakersNotes.vue';
import ChartToggle from '@frontend/ui/features/checkup/components/sidebar/ChartToggle.vue';
import AIChat from '@frontend/ui/features/checkup/components/sidebar/AIChat.vue';
import AreaHeader from '@frontend/ui/features/checkup/components/analytics/shared/AreaHeader.vue';
import AdvisorFeeInput from '@frontend/ui/features/checkup/components/AdvisorFeeInput.vue';
import CheckupMethodologyWrapper from '@frontend/ui/features/checkup/components/methodologies/CheckupMethodologyWrapper.vue';
import AreaFooter from '@frontend/ui/features/checkup/components/analytics/shared/AreaFooter.vue';

export default {
  name: 'MarketCapArea',
  components: {
    AreaPageContainer,
    AreaPageBody,
    ChartWrapper,
    ChartClickthroughs,
    CheckupDisclaimer,
    FeesMethodology,
    HighChartsWrapper,
    AreaSidebar,
    AdvisorCoreStripes,
    IncludeInPresentationToggle,
    SpeakersNotes,
    ChartToggle,
    AIChat,
    AreaHeader,
    AdvisorFeeInput,
    CheckupMethodologyWrapper,
    AreaFooter,

  },
  props: {
    isActiveTab: Boolean,
  },
  data() {
    return {
      expanded: false,
      GRADE_COLORS,
      chartClickData: null,
    };
  },
  computed: {
    ...mapStores(useChatStore, useCheckupStore),

    area() {
      return this.checkupStore.areasByName?.['Fees'];
    },
    // Tiered/flat-dollar fee schedules are gated behind this flag. When off, the
    // sidebar shows the legacy advisory-fee inputs inline instead of behind the
    // "Set custom fees" toggle.
    feesV2Enabled() {
      return this.checkupStore.featureFlags.includes('Feature: Advisor Fees');
    },
    // All row-type suffixes used to extract the portfolio name from an extraData entry name
    rowSuffixes() {
      return ['Avg Expense Ratio', 'Advisory Fee', 'Management Fee', 'Annual Flat Fee', 'Total Fees'];
    },
    portfolioHeaders() {
      if (!this.area?.extraData) return [];
      const seen = new Set();
      for (const entry of this.area.extraData) {
        for (const suffix of this.rowSuffixes) {
          if (entry.name.endsWith(suffix)) {
            const header = entry.name.slice(0, entry.name.length - suffix.length).trim();
            if (header) seen.add(header);
            break;
          }
        }
      }
      return [...seen];
    },
    feeTableRows() {
      const rows = [
        { key: 'avg', label: 'Avg Expense Ratio', suffix: 'Avg Expense Ratio', isTotal: false },
        // Match the pre-feature label ("and") when the flag is off.
        { key: 'advMgmt', label: this.feesV2Enabled ? 'Advisory & Mgmt Fees' : 'Advisory and Mgmt Fees', suffix: null, isTotal: false },
      ];
      if (this.area?.extraData?.some(d => d.name.includes('Annual Flat Fee'))) {
        rows.push({ key: 'flat', label: 'Annual Flat Fee', suffix: 'Annual Flat Fee', isTotal: false });
      }
      if (this.area?.extraData?.some(d => d.name.includes('Total Fees'))) {
        rows.push({ key: 'total', label: 'Total Fees', suffix: 'Total Fees', isTotal: true });
      }
      return rows;
    },
    benchmarkName() {
      const name = this.checkupStore.benchmark?.name;
      return name || 'Benchmark';
    },
  },

  watch: {
    // If you are having weird bugs where the active tab seems to be wrong or off by one, double check:
    // :is-active-tab="(i+3) === activeTab" in AutoPortfolioView.vue (the number 3 may be different)
    // 😭
    async isActiveTab() {
      if (this.isActiveTab) {
        const { name, current, proposal, grade } = this.area;
        // await this.chatStore.setContext(mockJSON);

        await this.chatStore.setContext({
          jsonData: {
            currentArea: {
              name,
              [this.checkupStore.portfolio.name]: current,
              [this.benchmarkName]: proposal,
              magnitudeOfDifference: grade,

              // Dear future developer, it would probably be better to have a chatContextData coming from the area object (for all areas).
              // That way it can be easily customized.
              // This is a band-aid to fix the chat for the Holdings Concentration tab.
              holdingsConcentrationBreakdown: name === 'Concentration Breakdown' ? this.checkupStore.portfolio.sector_market_cap_asset_class_breakdown?.['concentration_breakdown'] : undefined,
            },
            // TODO: Sort highest returns to lowest so we are giving the most important info to openai
            portfolio: {
              name: this.checkupStore.portfolio.name,
              holdings: this.checkupStore.portfolio.holdings.map(h => this.createContextFromHolding(h)),
            },
            benchmark: {
              name: this.benchmarkName,
              holdings: this.checkupStore.benchmark.holdings.map(h => this.createContextFromHolding(h)),
            },
          },
        });
      }
    },
  },
  methods: {
    makeValuesPercentages(obj) {
      if (!obj) return {};
      return Object.entries(obj).reduce((acc, [key, value]) => {
        acc[key] = `${((value || 0) * 100).toFixed(2)}%`;
        return acc;
      }, {});
    },
    createContextFromHolding(h) {
      return {
        symbol: h.ticker.symbol,
        dollars: h.dollars,
        returns: this.makeValuesPercentages(h.ticker.trailing_returns_monthly_rebalance),
        sectors: this.makeValuesPercentages(h.ticker.security_breakdown?.['Breakdown']),
        expenseRatio: `${(h.ticker.expense_ratio * 100).toFixed(2)}%`,
        assetClasses: this.makeValuesPercentages(h.ticker.security_breakdown?.['AssetType']),
        marketCap: this.makeValuesPercentages(h.ticker.security_breakdown?.['Market Cap']),
        trailingVolatilities: this.makeValuesPercentages(h.ticker.trailing_volatilities),
      };
    },
    // Maybe make callouts in parallel

    cellValue(portfolioHeader, suffix) {
      if (!this.area?.extraData) return '--';
      const entry = this.area.extraData.find(d => d.name === `${portfolioHeader} ${suffix}`);
      return entry?.value || '--';
    },
    advisoryMgmtCellValue(portfolioHeader) {
      const advStr = this.cellValue(portfolioHeader, 'Advisory Fee');
      const mgmtStr = this.cellValue(portfolioHeader, 'Management Fee');
      const adv = advStr !== '--' ? parseFloat(advStr) : 0;
      const mgmt = mgmtStr !== '--' ? parseFloat(mgmtStr) : 0;
      if (adv === 0 && mgmt === 0) return '--';
      return `${(adv + mgmt).toFixed(3)}%`;
    },
    handleChartClick(data) {
      this.chartClickData = data;
    },
    handleModalClick(rowName, portfolioName) {
      // Set chartClickData with only the portfolio name in the label
      this.chartClickData = {
        value: {
          label: portfolioName, // Set the portfolio name here
        },
      };
    },
  },
};
</script>


  <style scoped>

  </style>
